import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import i18n from '../../i18n/i18n';
import {
	getPendingFriendRequests,
	getPlayersDirectory,
	PendingFriendRequestsResponse,
	PlayersDirectoryResponse,
	removeFriend,
	respondToFriendRequest,
	sendFriendRequest,
} from '../../services/api';
import {
	directoryFailure,
	directoryRequest,
	directorySuccess,
	friendActionFailure,
	friendActionSuccess,
	friendRemove,
	friendRequestRespond,
	friendRequestSend,
	friendsListFailure,
	friendsListRequest,
	friendsListSuccess,
	pendingRequestsFailure,
	pendingRequestsRequest,
	pendingRequestsSuccess,
} from './friendsActions';
import type { FriendsState } from './friendsReducer';

/** Typing in a search box restarts this wait, so a word costs one request, not one per letter. */
const SEARCH_DEBOUNCE_MS = 300;

/** Translated when the error is built, never at module load. */
const fallbackMessage = () =>
	i18n.t('T_SITE_FRIENDS_ACTION_FAILED', {
		defaultValue: 'Could not update your friends. Please try again.',
	});

/** The friends service answers a refused action with a readable 4xx message; anything else is ours. */
const friendErrorMessage = (error: any): string => {
	const status = error?.response?.status;
	const message = error?.response?.data?.message;

	if (status >= 400 && status < 500 && typeof message === 'string') return message;
	return fallbackMessage();
};

function* fetchDirectoryWorker(action: ReturnType<typeof directoryRequest>) {
	const { filter, query, profile, append = false } = action.payload;
	if (!append && query) yield delay(SEARCH_DEBOUNCE_MS);

	try {
		const loaded: number = append
			? yield select((state: { friends: FriendsState }) => state.friends.directory.items.length)
			: 0;
		const response: PlayersDirectoryResponse = yield call(getPlayersDirectory, {
			filter,
			query: query.trim() || undefined,
			start: loaded,
			profile,
		});
		yield put(directorySuccess({ response, append }));
	} catch (error: any) {
		yield put(directoryFailure(friendErrorMessage(error)));
	}
}

function* fetchFriendsListWorker(action: ReturnType<typeof friendsListRequest>) {
	const { query } = action.payload;
	if (query) yield delay(SEARCH_DEBOUNCE_MS);

	try {
		const response: PlayersDirectoryResponse = yield call(getPlayersDirectory, {
			filter: 'FRIENDS',
			query: query.trim() || undefined,
		});
		yield put(friendsListSuccess({ response, query }));
	} catch (error: any) {
		yield put(friendsListFailure(friendErrorMessage(error)));
	}
}

function* fetchPendingWorker() {
	try {
		const response: PendingFriendRequestsResponse = yield call(getPendingFriendRequests);
		yield put(pendingRequestsSuccess(response));
	} catch {
		yield put(pendingRequestsFailure('pending'));
	}
}

function* refreshAfterChange() {
	yield put(pendingRequestsRequest());
	yield put(friendsListRequest({ query: '' }));
}

function* sendWorker(action: ReturnType<typeof friendRequestSend>) {
	const { playerId } = action.payload;

	try {
		const result: { id: string; status: string } = yield call(sendFriendRequest, playerId);
		const accepted = result.status === 'ACCEPTED';
		yield put(
			friendActionSuccess({
				playerId,
				status: accepted ? 'FRIENDS' : 'PENDING_OUTGOING',
				friendRequestId: accepted ? null : result.id,
			}),
		);
		if (accepted) yield* refreshAfterChange();
	} catch (error: any) {
		yield put(friendActionFailure({ playerId, message: friendErrorMessage(error) }));
	}
}

function* respondWorker(action: ReturnType<typeof friendRequestRespond>) {
	const { playerId, requestId, accept } = action.payload;

	try {
		yield call(respondToFriendRequest, requestId, accept ? 'ACCEPTED' : 'REJECTED');
		yield put(
			friendActionSuccess({ playerId, status: accept ? 'FRIENDS' : 'NONE', friendRequestId: null }),
		);
		yield* refreshAfterChange();
	} catch (error: any) {
		yield put(friendActionFailure({ playerId, message: friendErrorMessage(error) }));
	}
}

function* removeWorker(action: ReturnType<typeof friendRemove>) {
	const { playerId } = action.payload;

	try {
		yield call(removeFriend, playerId);
		yield put(friendActionSuccess({ playerId, status: 'NONE', friendRequestId: null }));
	} catch (error: any) {
		yield put(friendActionFailure({ playerId, message: friendErrorMessage(error) }));
	}
}

export function* friendsRootSaga() {
	yield takeLatest(directoryRequest.type, fetchDirectoryWorker);
	yield takeLatest(friendsListRequest.type, fetchFriendsListWorker);
	yield takeLatest(pendingRequestsRequest.type, fetchPendingWorker);
	yield takeEvery(friendRequestSend.type, sendWorker);
	yield takeEvery(friendRequestRespond.type, respondWorker);
	yield takeEvery(friendRemove.type, removeWorker);
}
